/**
 * @license Copyright (c) 2003-2018, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md.
 */
import 'ckeditor5/ckeditor5.css';
import 'ckeditor5-premium-features/ckeditor5-premium-features.css';

import {
	ClassicEditor,
	DecoupledEditor,
	Emoji,
	Essentials,
	AdjacentListsSupport,
	Autoformat,
	BlockQuote,
	Bold,
	CKFinder,
	CKFinderUploadAdapter,
	CloudServices,
	Code,
	CodeBlock,
	List,
	ListProperties,
	EasyImage,
	FindAndReplace,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	HtmlEmbed,
	AutoImage,
	Image,
	ImageCaption,
	ImageInsert,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	MediaEmbed,
	Mention,
	AutoLink,
	Link,
	LinkImage,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	RemoveFormat,
	Subscript,
	Superscript,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Table,
	TableCaption,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TextPartLanguage,
	SimpleUploadAdapter,
	Alignment,
	Font,
	Strikethrough,
	Underline,
} from 'ckeditor5';

import {
	Comments,
	ExportPdf,
	ExportWord,
	FormatPainter,
	ImportWord,
	PasteFromOfficeEnhanced,
	TableOfContents,
	Template,
	TrackChanges,
} from 'ckeditor5-premium-features';

import {
	EXPORT_HORIZONTAL_SPACE,
	EXPORT_VERTICAL_SPACE,
	CS_CONFIG,
	TEMPLATE_DEFINITIONS,
	LICENSE_KEY,
	HTML_SUPPORT_CONFIG,
	IMPORT_CONFIG,
} from './common';

class SimpleImport extends ClassicEditor {}
class ExtendedImport extends DecoupledEditor {}
class RestImport extends DecoupledEditor {}

SimpleImport.builtinPlugins = [
	AdjacentListsSupport,
	CloudServices,
	Emoji,
	Essentials,
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	CKFinder,
	CKFinderUploadAdapter,
	List,
	ListProperties,
	EasyImage,
	Font,
	Heading,
	Image,
	ImageCaption,
	ImageInsert,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	ImportWord,
	Indent,
	IndentBlock,
	Italic,
	Link,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	PasteFromOfficeEnhanced,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	SimpleUploadAdapter,
	Template,
	FormatPainter,
	TableOfContents,
	Mention
];

SimpleImport.defaultConfig = {
	cloudServices: CS_CONFIG,
	toolbar: {
		items: [
			'importWord',
			'|',
			'heading',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'|',
			'alignment',
			'|',
			'numberedList',
			'bulletedList',
			'|',
			'indent',
			'outdent',
			'|',
			'emoji',
			'specialCharacters',
			'link',
			'blockquote',
			'uploadImage',
			'insertTable',
			'pageBreak',
			'|',
			'undo',
			'redo',
			'|',
			'insertTemplate',
			'tableOfContents',
			'|',
			'formatPainter',
		],
		shouldNotGroupWhenFull: false,
	},
	emoji: {
		definitionsUrl: 'cdn'
	},
	heading: {
		options: [
			{
				model: 'paragraph',
				title: 'Paragraph',
				class: 'ck-heading_paragraph',
			},

			{
				model: 'heading1',
				view: 'h1',
				title: 'Heading 1',
				class: 'ck-heading_heading1',
			},
			{
				model: 'heading2',
				view: 'h2',
				title: 'Heading 2',
				class: 'ck-heading_heading2',
			},
			{
				model: 'heading3',
				view: 'h3',
				title: 'Heading 3',
				class: 'ck-heading_heading3',
			},
			{
				model: 'heading4',
				view: 'h4',
				title: 'Heading 4',
				class: 'ck-heading_heading4',
			},
			{
				model: 'heading5',
				view: 'h5',
				title: 'Heading 5',
				class: 'ck-heading_heading5',
			},
			{
				model: 'heading6',
				view: 'h6',
				title: 'Heading 6',
				class: 'ck-heading_heading6',
			},
		],
	},
	image: {
		toolbar: [
			'toggleImageCaption',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
		],
		styles: ['inline', 'block', 'side'],
	},
	placeholder:
		'Download a Word file, click on the Import from Word and upload the content into the editor.',

	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
		],
	},
	indentBlock: {
		offset: 1,
		unit: 'em',
	},
	language: 'en',
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [9, 10, 11, 12, 14, 15, 'default', 18, 20],
		supportAllValues: true,
	},
	exportPdf: {
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-demo-cke5.css'],
		appID: 'cke5-demo',
		converterOptions: {
			format: 'A4',
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
			page_orientation: 'portrait',
		},
		tokenUrl: false,
	},
	importWord: {
		tokenUrl: IMPORT_CONFIG.tokenUrl,
		converterUrl: IMPORT_CONFIG.converterUrl,
		formatting: {
			resets: 'none',
			defaults: 'none',
			styles: 'inline',
			comments: 'none',
		},
	},
	template: {
		definitions: TEMPLATE_DEFINITIONS,
	},
	licenseKey: LICENSE_KEY,
};

ExtendedImport.builtinPlugins = [
	AdjacentListsSupport,
	Alignment,
	Autoformat,
	AutoImage,
	AutoLink,
	BlockQuote,
	Bold,
	CloudServices,
	Code,
	CodeBlock,
	Comments,
	List,
	ListProperties,
	EasyImage,
	Emoji,
	Essentials,
	ExportPdf,
	ExportWord,
	FindAndReplace,
	Font,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	HtmlEmbed,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	ImportWord,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	MediaEmbed,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	PasteFromOfficeEnhanced,
	RemoveFormat,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextPartLanguage,
	TextTransformation,
	TrackChanges,
	Underline,
	SimpleUploadAdapter,
	Template,
	FormatPainter,
	TableOfContents,
	Mention
];

ExtendedImport.defaultConfig = {
	cloudServices: CS_CONFIG,
	toolbar: {
		items: [
			'importWord',
			'|',
			'undo',
			'redo',
			'|',
			'exportPdf',
			'exportWord',
			'|',
			'findAndReplace',
			'selectAll',
			'|',
			'heading',
			'|',
			'removeFormat',
			'bold',
			'italic',
			'strikethrough',
			'underline',
			'code',
			'subscript',
			'superscript',
			'|',
			'emoji',
			'specialCharacters',
			'horizontalLine',
			'pageBreak',
			'|',
			'highlight',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'-',
			'link',
			'blockQuote',
			'insertTable',
			'uploadImage',
			'mediaEmbed',
			'codeBlock',
			'htmlEmbed',
			'|',
			'bulletedList',
			'numberedList',
			'|',
			'outdent',
			'indent',
			'alignment',
			'|',
			'textPartLanguage',
			'|',
			'trackChanges',
			'comment',
			'commentsArchive',
			'|',
			'insertTemplate',
			'tableOfContents',
			'|',
			'formatPainter',
		],
		shouldNotGroupWhenFull: true,
	},
	emoji: {
		definitionsUrl: 'cdn'
	},
	heading: {
		options: [
			{
				model: 'paragraph',
				title: 'Paragraph',
				class: 'ck-heading_paragraph',
			},

			{
				model: 'heading1',
				view: 'h1',
				title: 'Heading 1',
				class: 'ck-heading_heading1',
			},
			{
				model: 'heading2',
				view: 'h2',
				title: 'Heading 2',
				class: 'ck-heading_heading2',
			},
			{
				model: 'heading3',
				view: 'h3',
				title: 'Heading 3',
				class: 'ck-heading_heading3',
			},
			{
				model: 'heading4',
				view: 'h4',
				title: 'Heading 4',
				class: 'ck-heading_heading4',
			},
			{
				model: 'heading5',
				view: 'h5',
				title: 'Heading 5',
				class: 'ck-heading_heading5',
			},
			{
				model: 'heading6',
				view: 'h6',
				title: 'Heading 6',
				class: 'ck-heading_heading6',
			},
		],
	},
	htmlSupport: HTML_SUPPORT_CONFIG,
	image: {
		resizeOptions: [
			{
				name: 'resizeImage:original',
				label: 'Original size',
				value: null,
			},
			{
				name: 'resizeImage:50',
				label: '50%',
				value: '50',
			},
			{
				name: 'resizeImage:75',
				label: '75%',
				value: '75',
			},
		],
		toolbar: [
			'imageTextAlternative',
			'toggleImageCaption',
			'|',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
			'|',
			'resizeImage',
			'|',
			'comment',
		],
	},
	link: {
		decorators: {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			toggleDownloadable: {
				mode: 'manual',
				label: 'Downloadable',
				attributes: {
					download: 'file',
				},
			},
		},
	},
	placeholder:
		'Download a Word file, click on the Import from Word and upload the content into the editor.',
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
			'toggleTableCaption',
		],
		tableToolbar: ['comment'],
	},
	indentBlock: {
		offset: 1,
		unit: 'em',
	},
	list: {
		properties: {
			styles: true,
			startIndex: true,
			reversed: true,
		},
	},
	language: 'en',
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [9, 10, 11, 12, 14, 15, 'default', 18, 20],
		supportAllValues: true,
	},
	mediaEmbed: {
		toolbar: ['comment'],
	},
	exportPdf: {
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-demo-cke5.css'],
		appID: 'cke5-demo',
		converterOptions: {
			format: 'A4',
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
			page_orientation: 'portrait',
		},
		tokenUrl: false,
	},
	exportWord: {
		version: 2,
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-word-cke5.css'],
		fileName: 'my-file.docx',
		converterOptions: {
			document: {
				size: 'A4', // Default value, you don't need to specify it explicitly for A4.
				margins: {
					top: EXPORT_VERTICAL_SPACE,
					bottom: EXPORT_VERTICAL_SPACE,
					right: EXPORT_HORIZONTAL_SPACE,
					left: EXPORT_HORIZONTAL_SPACE,
				},
			},
		},
		tokenUrl: false,
	},
	importWord: {
		tokenUrl: IMPORT_CONFIG.tokenUrl,
		converterUrl: IMPORT_CONFIG.converterUrl,
		formatting: {
			resets: 'none',
			defaults: 'inline',
			styles: 'inline',
			comments: 'basic',
		},
		collaborationFeatures: {
			userId: 'tester',
			comments: true,
			trackChanges: true,
		},
	},
	template: {
		definitions: TEMPLATE_DEFINITIONS,
	},
	comments: {
		editorConfig: {
			extraPlugins: [Autoformat, Bold, Italic, List],
		},
	},
	licenseKey: LICENSE_KEY,
};

RestImport.builtinPlugins = [
	AdjacentListsSupport,
	Alignment,
	Autoformat,
	AutoImage,
	AutoLink,
	BlockQuote,
	Bold,
	CloudServices,
	Code,
	CodeBlock,
	Comments,
	List,
	ListProperties,
	EasyImage,
	Emoji,
	Essentials,
	// ExportPdf,
	// ExportWord,
	FindAndReplace,
	Font,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	HtmlEmbed,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	ImportWord,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	MediaEmbed,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	PasteFromOfficeEnhanced,
	RemoveFormat,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextPartLanguage,
	TextTransformation,
	TrackChanges,
	Underline,
	SimpleUploadAdapter,
	Mention
];

RestImport.defaultConfig = {
	cloudServices: CS_CONFIG,
	toolbar: {
		items: [
			// 'importWord',
			// '|',
			'undo',
			'redo',
			// '|',
			// 'exportPdf',
			// 'exportWord',
			'|',
			'findAndReplace',
			// 'selectAll',
			'|',
			'heading',
			'|',
			'removeFormat',
			'bold',
			'italic',
			'strikethrough',
			'underline',
			'code',
			'subscript',
			'superscript',
			'|',
			'emoji',
			'specialCharacters',
			'horizontalLine',
			'pageBreak',
			'|',
			'highlight',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'-',
			'link',
			'blockQuote',
			'insertTable',
			'uploadImage',
			'mediaEmbed',
			'codeBlock',
			'htmlEmbed',
			'|',
			'bulletedList',
			'numberedList',
			'|',
			'outdent',
			'indent',
			'alignment',
			'|',
			'textPartLanguage',
			'|',
			'trackChanges',
			'comment',
		],
		shouldNotGroupWhenFull: true,
	},
	emoji: {
		definitionsUrl: 'cdn'
	},
	heading: {
		options: [
			{
				model: 'paragraph',
				title: 'Paragraph',
				class: 'ck-heading_paragraph',
			},

			{
				model: 'heading1',
				view: 'h1',
				title: 'Heading 1',
				class: 'ck-heading_heading1',
			},
			{
				model: 'heading2',
				view: 'h2',
				title: 'Heading 2',
				class: 'ck-heading_heading2',
			},
			{
				model: 'heading3',
				view: 'h3',
				title: 'Heading 3',
				class: 'ck-heading_heading3',
			},
			{
				model: 'heading4',
				view: 'h4',
				title: 'Heading 4',
				class: 'ck-heading_heading4',
			},
			{
				model: 'heading5',
				view: 'h5',
				title: 'Heading 5',
				class: 'ck-heading_heading5',
			},
			{
				model: 'heading6',
				view: 'h6',
				title: 'Heading 6',
				class: 'ck-heading_heading6',
			},
		],
	},
	htmlSupport: HTML_SUPPORT_CONFIG,
	image: {
		resizeOptions: [
			{
				name: 'resizeImage:original',
				label: 'Original size',
				value: null,
			},
			{
				name: 'resizeImage:50',
				label: '50%',
				value: '50',
			},
			{
				name: 'resizeImage:75',
				label: '75%',
				value: '75',
			},
		],
		toolbar: [
			'imageTextAlternative',
			'toggleImageCaption',
			'|',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
			'|',
			'resizeImage',
			'|',
			'comment',
		],
	},
	link: {
		decorators: {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			toggleDownloadable: {
				mode: 'manual',
				label: 'Downloadable',
				attributes: {
					download: 'file',
				},
			},
		},
	},
	placeholder: 'Processing file...',
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
			'toggleTableCaption',
		],
		tableToolbar: ['comment'],
	},
	indentBlock: {
		offset: 1,
		unit: 'em',
	},
	list: {
		properties: {
			styles: true,
			startIndex: true,
			reversed: true,
		},
	},
	language: 'en',
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [9, 10, 11, 12, 14, 15, 'default', 18, 20],
		supportAllValues: true,
	},
	mediaEmbed: {
		toolbar: ['comment'],
	},
	exportPdf: {
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-demo-cke5.css'],
		appID: 'cke5-demo',
		converterOptions: {
			format: 'A4',
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
			page_orientation: 'portrait',
		},
		tokenUrl: false,
	},
	exportWord: {
		version: 2,
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-word-cke5.css'],
		fileName: 'my-file.docx',
		converterOptions: {
			format: 'A4', // Default value, you don't need to specify it explicitly for A4.
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
		},
		tokenUrl: false,
	},
	importWord: {
		tokenUrl: IMPORT_CONFIG.tokenUrl,
		converterUrl: IMPORT_CONFIG.converterUrl,
		formatting: {
			resets: 'none',
			defaults: 'inline',
			styles: 'inline',
			comments: 'basic',
		},
		collaborationFeatures: {
			userId: 'tester',
			comments: true,
			trackChanges: true,
		},
	},
	licenseKey: LICENSE_KEY,
	comments: {
		editorConfig: {
			extraPlugins: [Autoformat, Bold, Italic, List],
		},
	},
};

export default {
	SimpleImport: SimpleImport,
	ExtendedImport: ExtendedImport,
	RestImport: RestImport,
};
